import { useState, useCallback } from 'react';
import { isAuthenticationError } from '../utils/authError';

/**
 * Custom hook for handling authentication errors consistently across the app
 * Provides state management and helper functions for auth error handling
 */
export function useAuthErrorHandler() {
  const [authError, setAuthError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Handle an error and check if it's an authentication error
   * Returns true if it's an auth error, false otherwise
   */
  const handleError = useCallback((error: any, defaultMessage?: string): boolean => {
    if (isAuthenticationError(error)) {
      setAuthError(true);
      setErrorMessage(
        error.message || 
        'Your session has expired. Please log in again to continue.'
      );
      return true;
    } else {
      setAuthError(false);
      setErrorMessage(
        error?.message || 
        defaultMessage || 
        'An error occurred. Please try again.'
      );
      return false;
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setAuthError(false);
    setErrorMessage(null);
  }, []);

  /**
   * Reset error state (useful when user logs in again)
   */
  const resetAuthError = useCallback(() => {
    setAuthError(false);
    // Keep error message until user explicitly clears it or logs in
  }, []);

  return {
    authError,
    errorMessage,
    handleError,
    clearError,
    resetAuthError,
    setErrorMessage
  };
}

